import { useCallback, useEffect, useRef, useState } from 'react'

interface PreviewDocument {
    fileName: string
    contentType?: string
}

/**
 * Fetch a document as a blob URL for previewing, revoking it on close/replace/unmount and ignoring stale responses.
 * `fetchUrl` receives an AbortSignal that is aborted when the preview closes or another one opens.
 */
export const useDocumentPreview = <D extends PreviewDocument>(onError?: (error: unknown) => void) => {
    const [preview, setPreview] = useState<D | null>(null)
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const urlRef = useRef<string | null>(null)
    const requestRef = useRef(0)
    const abortRef = useRef<AbortController | null>(null)

    // Invalidates the in-flight request, aborts it and revokes the current blob URL (no state updates, so it is unmount-safe).
    const discard = useCallback(() => {
        requestRef.current += 1
        abortRef.current?.abort()
        abortRef.current = null
        if (urlRef.current) URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
    }, [])

    // Stable identity, so callers can list it in effect dependencies.
    const closePreview = useCallback(() => {
        discard()
        setPreview(null)
        setBlobUrl(null)
    }, [discard])

    const openPreview = (document: D, fetchUrl: (signal: AbortSignal) => Promise<string>) => {
        discard()
        const requestId = requestRef.current
        const controller = new AbortController()
        abortRef.current = controller
        setPreview(document)
        setBlobUrl(null)
        void fetchUrl(controller.signal)
            .then((url) => {
                if (requestRef.current !== requestId) return URL.revokeObjectURL(url)
                urlRef.current = url
                setBlobUrl(url)
            })
            .catch((error: unknown) => {
                if (requestRef.current !== requestId) return
                setPreview(null)
                onError?.(error)
            })
    }

    useEffect(() => discard, [discard])

    return { preview, blobUrl, openPreview, closePreview }
}
